import backgroundImageUrl from 'assets/images/background-new-50368075.jpg';
import backgroundVideoUrl from 'assets/videos/background-new-50368075.mp4';

import { type FunctionComponent, useRef, useState } from 'react';
import { useNavigate } from 'react-router';

import { Button, Input } from '@dr.pogodin/react-utils';
import { captureException } from '@sentry/react';

import { PasswordResetChecks } from 'components';
import GlassCard from 'components/GlassCard';
import PageWithVideoBackground from 'components/PageWithVideoBackground';

import Throbber from 'components/Throbber';
import { usePasswordResetPageQuery } from 'hooks';

import { resetPasswordEnd } from 'services';
import { evalPasswordStrength, useGlobalState } from 'utils';

import IconEye from 'assets/images/icon-eye.svg';

import Logo from 'assets/images/logo-soul-sanctuary-new-150px.svg';

import buttonH52Theme from 'themes/Button/h52px.scss';
import cardTheme from 'themes/GlassCard/login.scss';

import buttonIconTheme from './showPasswordButton.scss';
import S from './style.scss';

export const PasswordReset: FunctionComponent = () => {
  const navigate = useNavigate();
  const [, setUser] = useGlobalState('user');

  const {
    email,
    firstName,
    lastName,
    token,
    userId,
  } = usePasswordResetPageQuery();

  const [passwordA, setPasswordA] = useState<string>('');
  const [passwordB, setPasswordB] = useState<string>('');

  const passwordInputRefB = useRef<HTMLInputElement>(null);

  const [showPasswordA, setShowPasswordA] = useState(false);
  const [showPasswordB, setShowPasswordB] = useState(false);

  const password = evalPasswordStrength(passwordA, [
    ...email.split('@'),
    firstName,
    lastName,
  ]);

  const ready: boolean = password.strength > 2 && passwordA === passwordB;

  const [busy, setBusy] = useState(false);

  const submit = async () => {
    try {
      setBusy(true);
      const user = await resetPasswordEnd({
        email,
        password: passwordA,
        token,
        userId,
      });
      setUser(user);
      void navigate('/');
    } catch (e) {
      captureException(e);
      // eslint-disable-next-line no-alert
      alert('Failed to set password');
    } finally {
      setBusy(false);
    }
  };

  return (
    <PageWithVideoBackground
      fallbackImageUrl={backgroundImageUrl}
      noAudioSetup
      videoUrl={backgroundVideoUrl}
    >
      <GlassCard theme={cardTheme}>
        <div className={S.content}>
          <Logo />
          <h1>Set a new password</h1>
          <form autoComplete="on" styleName="registrationForm">
            <input
              // This hidden input is necessary for the optimal auto-complete
              // and credentials store support.
              autoComplete="username"
              id="username"
              name="username"
              styleName="hidden"
              value={email}
            />
            <div styleName="inputWrapper">
              <Input
                autoComplete="new-password"
                id="passwordA"
                name="passwordA"
                onChange={(e) => {
                  setPasswordA(e.target.value);
                }}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') passwordInputRefB.current?.focus();
                }}
                placeholder="Password"
                type={showPasswordA ? 'text' : 'password'}
                value={passwordA}
              />
              <Button
                onClick={() => {
                  setShowPasswordA(!showPasswordA);
                }}
                theme={buttonIconTheme}
              >
                <IconEye />
              </Button>
            </div>
            <div styleName="inputWrapper">
              <Input
                autoComplete="new-password"
                id="passwordB"
                name="passwordB"
                onChange={(e) => {
                  setPasswordB(e.target.value);
                }}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    if (ready) void submit();
                  }
                }}
                placeholder="Repeat Password"
                ref={passwordInputRefB}
                type={showPasswordB ? 'text' : 'password'}
                value={passwordB}
              />
              <Button
                onClick={() => {
                  setShowPasswordB(!showPasswordB);
                }}
                theme={buttonIconTheme}
              >
                <IconEye />
              </Button>
            </div>
            <PasswordResetChecks
              passwordEvalRes={password}
              passwordsMatch={passwordA ? passwordA === passwordB : undefined}
            />
            {busy ? <div styleName="throbberContainer"><Throbber /></div> : (
              <Button
                disabled={!ready}
                onClick={() => {
                  void submit();
                }}
                theme={buttonH52Theme}
              >
                Set Password
              </Button>
            )}
          </form>
        </div>
      </GlassCard>
    </PageWithVideoBackground>
  );
};
